import { useEffect, useMemo, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { TrendingUp, BarChart3 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { AX } from '@/theme/adminTheme';
import Dh from '../components/Dh';
import { getAllUsers } from '@/utils/adminUsers';
import { purchasedPackages as pkgsOf } from '@/utils/purchases';
import { belongsToBrand } from '@/utils/adminScope';
import { BRAND } from '@/brand';

const IS_CORP = BRAND === 'corporate';
// Recharts needs a real colour value, not a CSS variable.
const ACCENT = AX.brand;

function last12Months() {
  const arr: { key: string; label: string }[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleDateString('en-US', { month: 'short' }) });
  }
  return arr;
}
const monthKey = (v?: string) => { if (!v) return ''; const d = new Date(v); return `${d.getFullYear()}-${d.getMonth()}`; };

// Cosmetic-only: prefix a flag when the location string is a recognizable country.
// Emirates / unknown strings simply render without a flag (matches the mockup's intent
// without inventing data).
const FLAGS: Record<string, string> = {
  'egypt': '🇪🇬', 'united arab emirates': '🇦🇪', 'uae': '🇦🇪',
  'saudi arabia': '🇸🇦', 'ksa': '🇸🇦', 'lebanon': '🇱🇧', 'jordan': '🇯🇴',
  'oman': '🇴🇲', 'pakistan': '🇵🇰', 'nigeria': '🇳🇬', 'india': '🇮🇳',
  'united kingdom': '🇬🇧', 'uk': '🇬🇧', 'united states': '🇺🇸', 'usa': '🇺🇸',
  'kuwait': '🇰🇼', 'qatar': '🇶🇦', 'bahrain': '🇧🇭', 'turkey': '🇹🇷',
  'germany': '🇩🇪', 'france': '🇫🇷', 'canada': '🇨🇦', 'spain': '🇪🇸',
  'italy': '🇮🇹', 'china': '🇨🇳', 'singapore': '🇸🇬',
};
const flagFor = (name: string) => FLAGS[name.trim().toLowerCase()] ?? '';

// The 5 accounting services a package's `flags` array maps to — same order as
// /my-services, AdminServices and the client detail view.
const SVC_NAMES = ['Bookkeeping', 'VAT Registration', 'VAT Filing', 'Corporate Tax Registration', 'Corporate Tax Filing'];

/** Where a sold line came from, so the ranking can be read at a glance. */
type SvcKind = 'Package' | 'Corporate' | 'Setup' | 'Add-on' | 'Other';
type SvcRow = { label: string; kind: SvcKind; value: number; revenue: number };

/** How many of the top lines the panel lists before folding the tail away. */
const SVC_TOP_N = 12;

// ── stat-tile glyphs (inline SVGs, matching the Level-6 mockup) ──
const ICONS = {
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.8-3.2 3.4-5 6.5-5s5.7 1.8 6.5 5" /></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M12 21a9 9 0 110-18 9 9 0 010 18z" /><path d="M12 12l4-3" /></svg>,
  target: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" /></svg>,
  card: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20M6 15h4" /></svg>,
  wallet: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="2" y="6" width="20" height="13" rx="2" /><path d="M2 11h20" /></svg>,
  layers: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5" /></svg>,
  trend: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></svg>,
};

// ── small presentational pieces (Poppins mockup design) ──
function StatTile({ icon, num, label, sub, numSize = 22 }: { icon: React.ReactNode; num: React.ReactNode; label: string; sub?: string; numSize?: number }) {
  return (
    <div className="stat">
      <span className="ico">{icon}</span>
      <div className="num tabular-nums" style={{ fontSize: numSize }}>{num}</div>
      <div className="lbl">{label}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

function Panel({ title, sub, children, bodyClass, style }: { title: string; sub?: string; children: React.ReactNode; bodyClass?: string; style?: React.CSSProperties }) {
  return (
    <div className="card" style={style}>
      <div className="card-h"><div><b>{title}</b><br /><span>{sub}</span></div></div>
      <div className={`card-b${bodyClass ? ` ${bodyClass}` : ''}`}>{children}</div>
    </div>
  );
}

function Bars({ data, cols, total, flags }: { data: { label: string; value: number }[]; cols: string; total?: number; flags?: boolean }) {
  const max = Math.max(1, ...data.map(d => d.value));
  return (
    <>
      {data.map(d => (
        <div key={d.label} className="bar-row" style={{ gridTemplateColumns: cols }}>
          <span className="lbl2">{flags ? `${flagFor(d.label)} ${d.label}`.trim() : d.label}</span>
          <div className="bar-track">
            <div className={`bar-fill${d.value === 0 ? ' zero' : ''}`} style={{ width: `${(d.value / max) * 100}%` }} />
          </div>
          <span className="val">{d.value}{total ? ` · ${Math.round((d.value / total) * 100)}%` : ''}</span>
        </div>
      ))}
    </>
  );
}

function SvcHead() {
  return (
    <div className="svc-row svc-head">
      <span />
      <span>Service</span>
      <span />
      <span>Units</span>
      <span>Booked value</span>
    </div>
  );
}

function SvcRows({ rows, total }: { rows: SvcRow[]; total: number }) {
  const max = Math.max(1, ...rows.map(r => r.value));
  return (
    <>
      {rows.map((r, i) => (
        <div key={`${r.kind}|${r.label}`} className="svc-row">
          <span className="rank">{i + 1}</span>
          <span className="name" title={r.label}>{r.label}<em>{r.kind}</em></span>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
          <span className="val">{r.value}{total ? ` · ${Math.round((r.value / total) * 100)}%` : ''}</span>
          {/* Only setup orders store a figure. A corporate bundle prices the
              bundle, not each service in it, so those lines stay blank rather
              than carrying a split we would have had to invent. */}
          <span className="rev">{r.revenue ? <><Dh />{r.revenue.toLocaleString()}</> : '—'}</span>
        </div>
      ))}
    </>
  );
}

function Donut({ data, total }: { data: { label: string; value: number }[]; total: number }) {
  const R = 62;
  const C = 2 * Math.PI * R;
  const first = data[0];
  const firstShare = first && total ? first.value / total : 0;
  const arc = C * firstShare;
  return (
    <>
      <svg width={170} height={170} viewBox="0 0 170 170" role="img" aria-label="Incorporate versus operate mix">
        <circle cx="85" cy="85" r={R} fill="none" stroke="var(--brand-soft)" strokeWidth={26} />
        {arc > 0 && (
          <circle cx="85" cy="85" r={R} fill="none" stroke="var(--brand)" strokeWidth={26}
            strokeDasharray={`${arc} ${C - arc}`} strokeDashoffset={C / 4} strokeLinecap="butt" />
        )}
        <text x="85" y="83" textAnchor="middle" fontSize="24" fontWeight="700" fill="var(--ink)">{total}</text>
        <text x="85" y="101" textAnchor="middle" fontSize="10" fill="var(--muted)">orders</text>
      </svg>
      <div className="legend">
        {data.map((d, i) => (
          <div key={d.label}>
            <span className="sw" style={i === 0 ? { background: 'var(--brand)' } : { background: 'var(--brand-soft)', border: '1px solid var(--brand-line)' }} />
            {d.label}: <b>{d.value}</b>
          </div>
        ))}
      </div>
    </>
  );
}

export default function AdminAnalytics() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { getAllUsers().then(d => setUsers(Array.isArray(d) ? d : [])).finally(() => setLoading(false)); }, []);

  const m = useMemo(() => {
    const scoped = users.filter(u => belongsToBrand(u, IS_CORP));
    const total = scoped.length;
    const paid = scoped.filter(u => u.paymentStatus === 'paid').length;
    const conversion = total ? Math.round((paid / total) * 100) : 0;

    const pkgRevenue = scoped.reduce((s, u) => s + pkgsOf(u).reduce((a, p) => a + (Number(p.price) || 0), 0), 0);
    const setupRevenue = scoped.reduce((s, u) => s + (u.businessSetupData?.total || 0), 0);
    const revenue = pkgRevenue + setupRevenue;
    // An "order" = one business-setup submission, matching Admin's "Setup orders" and
    // Accounting's "Total orders". Packages are counted only for the avg-order calc.
    const orderUnits = scoped.filter(u => u.businessSetupData).length + scoped.reduce((s, u) => s + pkgsOf(u).length, 0);
    const orders = scoped.filter(u => u.businessSetupData).length;
    const avgOrder = orderUnits ? Math.round(revenue / orderUnits) : 0;

    // Trend (12 months): revenue for corporate, new clients for consulting.
    const months = last12Months();
    const bucket: Record<string, number> = {};
    if (IS_CORP) {
      scoped.forEach(u => {
        if (u.businessSetupData?.submittedAt && u.businessSetupData.total) bucket[monthKey(u.businessSetupData.submittedAt)] = (bucket[monthKey(u.businessSetupData.submittedAt)] || 0) + u.businessSetupData.total;
        pkgsOf(u).forEach(p => { if (p.purchasedAt && p.price) bucket[monthKey(p.purchasedAt)] = (bucket[monthKey(p.purchasedAt)] || 0) + Number(p.price); });
      });
    } else {
      scoped.forEach(u => { if (u.createdAt) bucket[monthKey(u.createdAt)] = (bucket[monthKey(u.createdAt)] || 0) + 1; });
    }
    const trend = months.map(mo => ({ label: mo.label, value: bucket[mo.key] || 0 }));

    // ── What actually sells ────────────────────────────────────────────────
    // One line per thing a client bought, from the same ledger AdminServices
    // reads. Three shapes land in it:
    //   · an accounting package — carries `flags`, so it also tells us which of
    //     the five accounting services it covers;
    //   · a corporate request  — carries `services`, a list of named services,
    //     each of which is counted on its own (the bundle name is not a service);
    //   · a business-setup order, plus its expedite / upgrade add-ons.
    const svcMap = new Map<string, SvcRow>();
    const addSvc = (label: string, kind: SvcKind, revenue = 0) => {
      const clean = String(label || '').trim();
      if (!clean) return;
      const key = `${kind}|${clean}`;
      const row = svcMap.get(key);
      if (row) { row.value += 1; row.revenue += revenue; }
      else svcMap.set(key, { label: clean, kind, value: 1, revenue });
    };

    const coverage = SVC_NAMES.map(label => ({ label, value: 0 }));
    let pkgCount = 0;

    scoped.forEach(u => {
      pkgsOf(u).forEach(p => {
        const price = Number(p.price) || 0;
        if (Array.isArray(p.services) && p.services.length) {
          p.services.forEach((label: any) => addSvc(label, 'Corporate'));
        } else if (Array.isArray(p.flags)) {
          addSvc(p.name, 'Package', price);
          pkgCount += 1;
          p.flags.forEach((f: any, i: number) => {
            const row = coverage[i];
            if (row && f === 1) row.value += 1;
          });
        } else {
          addSvc(p.name, 'Other', price);
        }
      });

      const b = u.businessSetupData;
      if (b && (b.submittedAt || b.total != null || b.planName)) {
        const label = b.planName
          || (b.type === 'incorporate' ? 'Company setup — incorporate'
            : b.type === 'operate' ? 'Company setup — operate'
              : 'Company setup');
        addSvc(label, 'Setup', Number(b.total) || 0);
        if (b.hasExpedited) addSvc('Expedited processing', 'Add-on');
        if (b.hasUpgrade) addSvc('Total Compliance upgrade', 'Add-on');
      }
    });

    const services = [...svcMap.values()].sort((a, b) => b.value - a.value || b.revenue - a.revenue);
    const svcUnits = services.reduce((sum, r) => sum + r.value, 0);
    const svcTop = services.slice(0, SVC_TOP_N);
    const svcTail = services.length - svcTop.length;
    const coverageRows = coverage.filter(c => c.value > 0);

    // Distributions
    const pipeline = ['new', 'in_progress', 'awaiting_docs', 'delivered', 'cancelled'].map(k => ({
      label: ({ new: 'New', in_progress: 'In progress', awaiting_docs: 'Awaiting docs', delivered: 'Delivered', cancelled: 'Cancelled' } as any)[k],
      value: scoped.filter(u => (u.orderStatus || (u.businessSetupData ? 'new' : null)) === k).length,
    })).filter(d => d.value > 0);

    const setupMix = [
      { label: 'Incorporate', value: scoped.filter(u => u.businessSetupData?.type === 'incorporate').length },
      { label: 'Operate', value: scoped.filter(u => u.businessSetupData?.type === 'operate').length },
    ].filter(d => d.value > 0);
    const setupTotal = setupMix.reduce((s, d) => s + d.value, 0);

    // Consulting funnel + distributions
    const goalDone = scoped.filter(u => u.goalAssessment?.group).length;
    const irDone = scoped.filter(u => u.irScore).length;
    const crDone = scoped.filter(u => u.capitalReadiness).length;
    const funnel = [
      { label: 'Signed up', value: total },
      { label: 'Goal set', value: goalDone },
      { label: 'IR scored', value: irDone },
      { label: 'Capital readiness', value: crDone },
    ];
    const goalNames: Record<string, string> = { G1: 'Capital Readiness', G2: 'MVP Launch', G3: 'Revenue Growth', G4: 'Governance' };
    const goalDist = ['G1', 'G2', 'G3', 'G4'].map(g => ({ label: goalNames[g] ?? g, value: scoped.filter(u => u.goalAssessment?.group === g).length })).filter(d => d.value > 0);

    // New this month (rolling 30 days by signup) + clients-by-country (top 10).
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    const newThisMonth = scoped.filter(u => u.createdAt && new Date(u.createdAt) >= cutoff).length;
    const countryCount: Record<string, number> = {};
    // Consulting clients set `country` (Countrysl); corporate clients set an `emirate`
    // during business setup — use whichever exists so the panel isn't empty per brand.
    scoped.forEach(u => { const c = String(u.country || u.businessSetupData?.emirate || '').trim(); if (c) countryCount[c] = (countryCount[c] || 0) + 1; });
    const countryDist = Object.entries(countryCount).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 10);
    const noGoal = total - goalDone;

    // "Assessments" = completed Capital Readiness assessments (same field as the funnel's
    // "Capital readiness" step) so the KPI and the funnel always agree. Older code read a
    // dead `capitalAssessment.completedAt` field the app never writes, which undercounted.
    return { total, paid, conversion, revenue, orders, orderUnits, avgOrder, trend, pipeline, setupMix, setupTotal, funnel, goalDist, assessments: crDone, irDone, newThisMonth, countryDist, goalDone, noGoal, svcTop, svcUnits, svcTail, svcCount: services.length, coverageRows, pkgCount };
  }, [users]);

  const pct = (n: number) => (m.total ? `${Math.round((n / m.total) * 100)}% of clients` : undefined);

  return (
    <AdminLayout
      section="Overview"
      title="Analytics"
      icon={BarChart3}
      subtitle={IS_CORP
        ? 'Revenue, conversion and pipeline across your clients.'
        : 'Sign-ups, readiness and conversion across your book.'}
      spaced
    >
      {/* Page-specific only — tokens, cards, stat tiles and the shared states
          live in styles/adminShell.css. */}
      <style>{`
        .adminx .stat .ico { width: 34px; height: 34px; border-radius: 9px; background: var(--brand-soft); color: var(--brand-d); display: grid; place-items: center; margin-bottom: 12px; }
        .adminx .stat .ico svg { width: 17px; height: 17px; }
        .adminx .stat .num { font-weight: 700; line-height: 1.15; color: var(--ax-ink); }
        .adminx .stat .lbl { font-size: 12px; color: var(--ax-muted); margin-top: 2px; }
        .adminx .stat .sub { font-size: 11px; color: var(--ax-faint); margin-top: 4px; }

        .adminx .split-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; align-items: start; }
        @media (max-width: 900px) { .adminx .split-2 { grid-template-columns: 1fr; } }

        .adminx .bar-row { display: grid; grid-template-columns: 110px 1fr 34px; align-items: center; gap: 12px; padding: 7px 0; font-size: 12.5px; }
        .adminx .bar-row .lbl2 { color: #334155; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .adminx .bar-track { background: var(--ax-line-2); border-radius: 999px; height: 10px; overflow: hidden; }
        .adminx .bar-fill { height: 100%; border-radius: 999px; background: var(--brand); }
        .adminx .bar-fill.zero { background: transparent; }
        .adminx .bar-row .val { font-weight: 600; text-align: right; color: var(--ax-ink); font-variant-numeric: tabular-nums; white-space: nowrap; }

        .adminx .svc-row { display: grid; grid-template-columns: 22px minmax(120px, 1.5fr) 1fr 82px 104px; align-items: center; gap: 12px; padding: 8px 0; font-size: 12.5px; border-bottom: 1px solid var(--ax-line-2); }
        .adminx .svc-row:last-child { border-bottom: none; }
        .adminx .svc-row .rank { color: var(--ax-faint); font-weight: 700; font-variant-numeric: tabular-nums; }
        .adminx .svc-row .name { color: #334155; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .adminx .svc-row .name em { font-style: normal; margin-left: 8px; font-size: 9.5px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; padding: 2px 6px; border-radius: 999px; background: var(--ax-line-2); color: var(--ax-muted); }
        .adminx .svc-row .val, .adminx .svc-row .rev { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .adminx .svc-row .val { font-weight: 600; color: var(--ax-ink); }
        .adminx .svc-row .rev { color: var(--ax-muted); }
        .adminx .svc-head { padding-bottom: 6px; font-size: 10px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ax-faint); }
        .adminx .svc-head span { text-align: right; }
        .adminx .svc-head span:nth-child(2) { text-align: left; }
        /* Narrow screens keep the ranking and the counts — the bar and the value column go. */
        @media (max-width: 760px) {
          .adminx .svc-row { grid-template-columns: 20px 1fr 74px; }
          .adminx .svc-row .bar-track, .adminx .svc-row .rev, .adminx .svc-head span:nth-child(3), .adminx .svc-head span:nth-child(5) { display: none; }
        }

        .adminx .donut-wrap { display: flex; align-items: center; gap: 26px; justify-content: center; flex-wrap: wrap; }
        .adminx .legend { display: flex; flex-direction: column; gap: 9px; font-size: 12.5px; color: #334155; }
        .adminx .legend .sw { width: 10px; height: 10px; border-radius: 3px; display: inline-block; margin-right: 7px; }      `}</style>

          {loading ? (
            <div className="flex items-center justify-center py-24"><div className="ax-spinner" /></div>
          ) : (
            <>
              {/* Stat tiles */}
              <div className="stats">
                {IS_CORP ? (
                  <>
                    <StatTile icon={ICONS.wallet} num={<><Dh />{m.revenue.toLocaleString()}</>} numSize={21} label="Booked order value" sub="Excl. cancelled · all time" />
                    <StatTile icon={ICONS.layers} num={m.orderUnits} numSize={21} label="Orders / services" sub="Excl. cancelled · all time" />
                    <StatTile icon={ICONS.card} num={`${m.paid} of ${m.total}`} numSize={21} label="Paid clients" sub={`${m.conversion}% of all clients`} />
                    <StatTile icon={ICONS.trend} num={<><Dh />{m.avgOrder.toLocaleString()}</>} numSize={21} label="Avg order value" sub="Per booked order" />
                  </>
                ) : (
                  <>
                    <StatTile icon={ICONS.users} num={m.total} label="Total clients" sub="All time" />
                    <StatTile icon={ICONS.clock} num={m.irDone} label="IR scores done" sub={pct(m.irDone)} />
                    <StatTile icon={ICONS.target} num={m.assessments} label="Capital assessments" sub={pct(m.assessments)} />
                    <StatTile icon={ICONS.card} num={`${m.paid} of ${m.total}`} label="Paid clients" sub={`${m.conversion}% conversion`} />
                  </>
                )}
              </div>

              {/* Trend */}
              <Panel
                title={IS_CORP ? 'Revenue' : 'New clients'}
                sub={IS_CORP ? 'AED · booked order value by month · last 12 months' : 'Sign-ups by month · last 12 months'}
              >
                <div style={{ width: '100%', height: 250 }}>
                  <ResponsiveContainer>
                    <AreaChart data={m.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="anGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={ACCENT} stopOpacity={0.25} />
                          <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#EFF1F4" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={IS_CORP ? 56 : 40} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: '#fff', borderRadius: 10, border: '1px solid #E6E9ED', boxShadow: '0 12px 28px -12px rgba(15,23,42,.18)', fontSize: 12 }}
                        itemStyle={{ color: ACCENT, fontWeight: 700 }}
                        formatter={(v: any) => [IS_CORP ? `AED ${Number(v).toLocaleString()}` : `${v}`, IS_CORP ? 'Revenue' : 'New clients']}
                      />
                      <Area type="monotone" dataKey="value" stroke={ACCENT} strokeWidth={2.5} strokeLinejoin="round" fill="url(#anGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              {/* Split: pipeline / funnel + mix */}
              <div className="split-2">
                {IS_CORP ? (
                  <>
                    <Panel title="Order pipeline" sub="All submissions by stage · all time">
                      {m.pipeline.length ? <Bars data={m.pipeline} cols="110px 1fr 34px" /> : <p className="empty">No orders yet.</p>}
                    </Panel>
                    <Panel title="Setup mix" sub="Incorporate vs operate · all submissions" bodyClass="donut-wrap">
                      {m.setupMix.length ? <Donut data={m.setupMix} total={m.setupTotal} /> : <p className="empty">No setup orders yet.</p>}
                    </Panel>
                  </>
                ) : (
                  <>
                    <Panel title="Readiness funnel" sub="How far clients progress · all time">
                      <Bars data={m.funnel} cols="130px 1fr 40px" />
                    </Panel>
                    <Panel title="Goal focus" sub="Distribution of set client goals · all time">
                      {m.goalDist.length ? (
                        <>
                          <Bars data={m.goalDist} cols="130px 1fr 40px" />
                          <p className="hint" style={{ marginTop: 10 }}>{m.goalDone} clients have set a goal · {m.noGoal} haven’t yet.</p>
                        </>
                      ) : <p className="empty">No goal assessments yet.</p>}
                    </Panel>
                  </>
                )}
              </div>

              {/* Most bought services */}
              <Panel
                title="Most bought services"
                sub={m.svcUnits
                  ? `Ranked by units sold · ${m.svcUnits} purchased line${m.svcUnits === 1 ? '' : 's'} across ${m.svcCount} service${m.svcCount === 1 ? '' : 's'} · all time`
                  : 'Ranked by units sold · all time'}
              >
                {m.svcTop.length ? (
                  <>
                    <SvcHead />
                    <SvcRows rows={m.svcTop} total={m.svcUnits} />
                    {m.svcTail > 0 && (
                      <p className="hint" style={{ marginTop: 10 }}>
                        + {m.svcTail} more service{m.svcTail === 1 ? '' : 's'} with fewer units.
                      </p>
                    )}
                  </>
                ) : <p className="empty">Nothing bought yet.</p>}
              </Panel>

              {/* What the packages that sold actually cover */}
              {IS_CORP && (
                <Panel
                  title="Services inside those packages"
                  sub="How often each accounting service is included · all purchased packages"
                >
                  {m.coverageRows.length ? (
                    <>
                      <Bars data={m.coverageRows} cols="200px 1fr 74px" total={m.pkgCount} />
                      <p className="hint" style={{ marginTop: 10 }}>
                        Share of the {m.pkgCount} accounting package{m.pkgCount === 1 ? '' : 's'} sold. One package usually covers several services.
                      </p>
                    </>
                  ) : <p className="empty">No accounting packages sold yet.</p>}
                </Panel>
              )}

              {/* Clients by country */}
              <Panel
                title="Clients by country"
                sub={IS_CORP ? 'Unique clients · all submissions · all time' : 'Where your book is based · all time'}
              >
                {m.countryDist.length
                  ? <Bars data={m.countryDist} cols="200px 1fr 74px" total={m.total} flags />
                  : <p className="empty">No location data captured yet.</p>}
              </Panel>
            </>
          )}
    </AdminLayout>
  );
}
